import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useApiQuery } from 'grabbitbot-legacy-ui'
import { adminFinance } from '../api/finance'
import type { FinanceScope } from '../api/financeTypes'
import { tokenStorage } from '../api/client'
import {
  getStoredMode,
  getStoredProject,
  projectParam,
  resolveSelection,
  storeMode,
  storeProject,
  switchVisible,
  type ViewMode,
} from '../lib/financeMode'
import { FinanceCtx } from './financeContext'
import { readScopeCache, writeScopeCache } from './scopeCache'

export const ADMIN_HOME = '/admin/finance'
const isAdminPath = (p: string) => p === ADMIN_HOME || p.startsWith(`${ADMIN_HOME}/`)

/**
 * Loads `GET /admin/finance/scope` once for the signed-in viewer and owns the
 * view mode + project selection. The mode is decided by WHERE the viewer is
 * (an `/admin/finance/*` URL is admin mode) so a deep link always renders
 * consistently; the stored choice only decides where `/projects` — the normal
 * landing page — sends the viewer on a fresh page load. A failed scope call
 * (older API, network) simply means "no switch": the viewer keeps the normal
 * user pages.
 */
export function FinanceProvider({ children }: { children: ReactNode }) {
  const q = useApiQuery(() => adminFinance.scope(), [])
  const token = tokenStorage.get()
  // Each protected route mounts its own provider; the last answer (per token)
  // keeps the switch from disappearing while the next section re-asks.
  const [cached] = useState<FinanceScope | null>(() => readScopeCache(token))
  const fresh = q.data
  useEffect(() => {
    if (fresh) writeScopeCache(token, fresh)
  }, [fresh, token])
  const scope = fresh ?? (q.error ? null : cached)
  const visible = switchVisible(scope)
  const location = useLocation()
  const navigate = useNavigate()
  const [selected, setSelected] = useState<string | null>(getStoredProject)
  const landed = useRef(false)

  const onAdmin = isAdminPath(location.pathname)
  const mode: ViewMode = visible && onAdmin ? 'admin' : 'user'

  useEffect(() => {
    if (!visible) return
    if (onAdmin) storeMode('admin')
    else if (location.pathname.replace(/\/$/, '') === '/projects') {
      if (!landed.current && getStoredMode() === 'admin') navigate(ADMIN_HOME, { replace: true })
      else storeMode('user')
    } else storeMode('user')
    landed.current = true
  }, [visible, onAdmin, location.pathname, navigate])

  const setMode = useCallback(
    (m: ViewMode) => {
      storeMode(m)
      navigate(m === 'admin' ? ADMIN_HOME : '/projects')
    },
    [navigate],
  )
  const setSelection = useCallback((value: string) => {
    storeProject(value)
    setSelected(value)
  }, [])

  const value = useMemo(() => {
    const selection = scope ? resolveSelection(scope, selected) : ''
    return {
      scope,
      scopeLoading: q.loading && !scope,
      scopeError: q.error,
      visible,
      mode,
      setMode,
      selection,
      setSelection,
      projectId: scope ? projectParam(scope, selection) : undefined,
    }
  }, [scope, selected, q.loading, q.error, visible, mode, setMode, setSelection])

  return <FinanceCtx.Provider value={value}>{children}</FinanceCtx.Provider>
}
